import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { DialogActions, DialogBody, DialogTitle, DialogTrigger, makeStyles, mergeClasses } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { TimeMutationDto, TimeOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { FormikConfig, useFormikContext } from 'formik'
import {
    bundleIcon,
    DeleteRegular,
    DismissFilled,
    DismissRegular,
    SaveEditFilled,
    SaveEditRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import TimeMutationDialogBodyContent from './TimeMutationDialogBodyContent'
import TooltippedButton from '../../Button/TooltippedButton'
import { useCounter } from 'usehooks-ts'

const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const SaveAndNewIcon = bundleIcon(SaveEditFilled, SaveEditRegular)

const useStyles = makeStyles({
    actions: {
        width: '100%',
    },
})

export default function TimeMutationDialogBody(
    props: HammockFormDialogProps<TimeMutationDto, BaseMutableOverviewDialogProps<TimeOverviewDto>>,
): ReactElement {
    const axios = useAxios()

    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const { t: translate } = useTranslation()

    const [submitVariant, setSubmitVariant] = useState<'save' | 'saveAndNew'>('save')
    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)
    const { initialValues, submitForm, isSubmitting, resetForm } = useFormikContext<TimeMutationDto>()

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<TimeOverviewDto>), [props])

    const handleSubmit = useCallback<FormikConfig<TimeMutationDto>['onSubmit']>(
        values =>
            dialogBodyProps.id
                ? axios.put(`/time/${dialogBodyProps.id}`, values)
                : axios
                      .post(`/time`, values)
                      .then(() => {
                          if (submitVariant === 'saveAndNew') {
                              resetForm({
                                  values: {
                                      ...initialValues,
                                      category: values.category,
                                      currency: values.currency,
                                      date: values.date,
                                      rate: values.rate,
                                      fileId: values.fileId,
                                      userId: values.userId,
                                      billable: values.billable,
                                  },
                              })
                              updateFocusTrigger()
                          } else {
                              resetForm()
                          }
                      })
                      .then(() => submitVariant === 'saveAndNew'),
        [submitVariant],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    const handleDelete = () =>
        axios
            .delete(`/time/${dialogBodyProps.id}`)
            .then(() => resetForm())
            .then(() => props.onDismiss())
    const handleCancel = () => {
        resetForm()
        props.onDismiss()
    }

    useEffect(() => updateFocusTrigger(), [])

    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    return (
        <DialogBody>
            <DialogTitle>{dialogBodyProps.id ? translate('time.edit.dialog.edit') : translate('time.edit.dialog.create')}</DialogTitle>
            <TimeMutationDialogBodyContent {...props} focusTrigger={focusTrigger} updateFocusTrigger={updateFocusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    {dialogBodyProps.id && (
                        <TooltippedButton
                            appearance="subtle"
                            display="iconOnly"
                            label={translate('action.delete')}
                            icon={<DeleteRegular />}
                            onClick={() => handleDelete()}
                            disabled={isSubmitting || dialogBodyProps.item?.invoice?.status !== undefined}
                        />
                    )}
                    {!dialogBodyProps.id && (
                        <TooltippedButton
                            appearance="subtle"
                            label={translate('action.saveandnew')}
                            icon={<SaveAndNewIcon />}
                            onClick={() => {
                                setSubmitVariant('saveAndNew')
                                submitForm()
                            }}
                            disabled={isSubmitting || isInvoiceFinal}
                        />
                    )}
                    {!dialogBodyProps.id && <div></div>}
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <TooltippedButton
                            appearance="primary"
                            label={translate('action.save')}
                            icon={<SaveIcon />}
                            onClick={() => {
                                setSubmitVariant('save')
                                submitForm()
                            }}
                            disabled={isSubmitting || isInvoiceFinal}
                        />
                        <DialogTrigger disableButtonEnhancement>
                            <TooltippedButton
                                appearance="secondary"
                                label={translate('action.cancel')}
                                icon={<CloseIcon />}
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            />
                        </DialogTrigger>
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
